import { Injectable } from '@angular/core';
import { MembershipApiService } from '@features/membership/services/membership-api/membership-api.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { DialogConfig } from '@rd/popup/dialog-config';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { HttpError, Membership } from '@rd/types';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  MembershipFailDialogConfig,
  TcV2MembershipFailDialogComponent,
} from '../../components/membership-fail-dialog/membership-fail-dialog.component';
import { TcV2MembershipSuccessDialogComponent } from '../../components/membership-success-dialog/membership-success-dialog.component';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import * as actions from '../actions/membership.actions';

@Injectable()
export class MembershipEffects {
  constructor(
    private actions$: Actions,
    private dialogService: DialogService,
    private membershipApiService: MembershipApiService,
    private tcV2SettingService: TransferConnectV2SettingService,
    private translateService: TranslateService,
  ) {}

  loadMemberships$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.LOAD_MEMBERSHIPS),
      switchMap(() => {
        return this.membershipApiService.getAllMemberships().pipe(
          map((memberships: Membership[]) => {
            return new actions.LoadMembershipsSuccess(memberships);
          }),
          catchError((error: HttpError) =>
            of(new actions.LoadMembershipsFail(error)),
          ),
        );
      }),
    ),
  );

  createMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.CREATE_MEMBERSHIP),
      switchMap((action: actions.CreateMembership) => {
        return this.membershipApiService.createMembership(action.payload).pipe(
          switchMap((res) => {
            this.dialogService.open(TcV2MembershipSuccessDialogComponent, {
              id: 'tcv2-membership-success-dialog',
              ariaLabel: this.translateService.instant(
                'transfer_v2.membership_success_dialog.title',
              ),
              screenMode: 'center-md',
              data: {
                loyaltyProgramShortName: action.payload.loyaltyProgramShortName,
              },
            });

            return [
              new actions.CreateMembershipSuccess(res),
              new actions.LoadMemberships(), // Upon creating a membership, re-dispatch LoadMemberships to update Memberships entirely again
            ];
          }),
          catchError((error: HttpError) => {
            this.dialogService.open(
              TcV2MembershipFailDialogComponent,
              this.failDialogComponentParams({
                context: 'number',
                loyaltyProgramShortName: action.payload.loyaltyProgramShortName,
              }),
            );

            return of(new actions.CreateMembershipFail(error));
          }),
        );
      }),
    ),
  );

  updateMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.UPDATE_MEMBERSHIP),
      switchMap((action: actions.UpdateMembership) => {
        return this.membershipApiService
          .updateMembership(action.payload.updateMembershipData)
          .pipe(
            switchMap((res) => {
              if ('number' in action.payload.updateMembershipData.membership) {
                {
                  this.dialogService.open(
                    TcV2MembershipSuccessDialogComponent,
                    {
                      id: 'tcv2-membership-success-dialog',
                      ariaLabel: this.translateService.instant(
                        'transfer_v2.membership_success_dialog.title',
                      ),
                      screenMode: 'center-md',
                      data: {
                        loyaltyProgramShortName:
                          action.payload.updateMembershipData
                            .loyaltyProgramShortName,
                      },
                    },
                  );
                }
              }

              return [
                new actions.UpdateMembershipSuccess({
                  updateMembershipData: action.payload.updateMembershipData,
                  membership: res,
                }),
                new actions.LoadMemberships(),
              ];
            }),
            catchError((error: HttpError) => {
              if ('number' in action.payload.updateMembershipData.membership) {
                this.dialogService.open(
                  TcV2MembershipFailDialogComponent,
                  this.failDialogComponentParams({
                    context: 'number',
                    loyaltyProgramShortName:
                      action.payload.updateMembershipData
                        .loyaltyProgramShortName,
                  }),
                );
              }

              return of(new actions.UpdateMembershipFail(error));
            }),
          );
      }),
    ),
  );

  private failDialogComponentParams(params: {
    context: MembershipFailDialogConfig['context'];
    loyaltyProgramShortName?: string;
  }): {
    data: MembershipFailDialogConfig;
    id: 'tcv2-membership-fail-dialog';
    ariaLabel: string;
    screenMode: DialogConfig['screenMode'];
  } {
    return {
      data: params,
      id: 'tcv2-membership-fail-dialog',
      ariaLabel: this.translateService.instant(
        'transfer_v2.membership_fail_dialog.number_title',
      ),
      screenMode: 'center-md',
    };
  }
}
